// The Summary tab: the template's Quotation Summary (a row per area) and
// Repaint Breakdown by Jobpac cost code, with the price and the Price Check
// (STM002 Rev 41 Summary sheet). Read-only; it shows the option being priced.
import type { ReactNode } from 'react';
import { breakdownGroups, type PricedOption, type SectionInfo } from '@higgins/m2q-pricing';
import { formatMoney, formatPercent, formatSell, formatTwo } from './surface-format';
import { Notice } from './message';
import { HoursPricedLine } from './hours-priced';

/** Repaint is year 1 in the template's summary; maintenance years come with maintenance options. */
const REPAINT_YEAR = 1;
/** Below this GP the template asks for management approval (the gp.floor setting, read when known). */
const DEFAULT_GP_FLOOR = 35;
const PERCENT = 100;

const num = 'px-2 py-1 text-right tabular-nums whitespace-nowrap';
const head = 'px-2 py-1.5 text-xs font-normal text-slate-500';

interface Props {
  option: PricedOption;
  info: ReadonlyMap<string, SectionInfo>;
  gpFloor: number | undefined;
  /** REVIEW questions not yet answered Yes that hold back the sell price (the template's REVIEW!M86 gate). */
  blockers: string[];
  /** The "client accepted this option" control (accept-panel.tsx), shown in the price box. */
  accept: ReactNode;
}

function AreaSummary({ option, info, priceShown }: Pick<Props, 'option' | 'info'> & { priceShown: boolean }) {
  const totals = option.sections.reduce(
    (t, s) => ({ hours: t.hours + s.hours, overtime: t.overtime + s.overtimeHours, abseil: t.abseil + s.abseilHours, litres: t.litres + s.litres, sell: t.sell + s.sell }),
    { hours: 0, overtime: 0, abseil: 0, litres: 0, sell: 0 },
  );
  return (
    <table className="w-full text-sm">
      <thead>
        <tr>
          <th className={`${head} text-left`} colSpan={2}>Area</th>
          {['Repaint Year', 'Repaint Hours', 'Overtime Hours (incl.)', 'Abseil Hours', 'Repaint Litres', 'Repaint Price ($)'].map((h) => (
            <th key={h} className={`${head} text-right`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {option.sections.map((s) => (
          <tr key={s.id} className="border-t border-slate-100">
            <td className="px-2 py-1 text-slate-500">{info.get(s.id)?.side ?? ''}</td>
            <td className="px-2 py-1">{s.name}</td>
            <td className={num}>{REPAINT_YEAR}</td>
            <td className={num}>{formatTwo(s.hours)}</td>
            <td className={num}>{formatTwo(s.overtimeHours)}</td>
            <td className={num}>{formatTwo(s.abseilHours)}</td>
            <td className={num}>{formatTwo(s.litres)}</td>
            <td className={num}>{formatMoney(s.sell)}</td>
          </tr>
        ))}
        <tr className="border-t border-slate-300 font-semibold">
          <td className="px-2 py-1" colSpan={3}>Total</td>
          <td className={num}>{formatTwo(totals.hours)}</td>
          <td className={num}>{formatTwo(totals.overtime)}</td>
          <td className={num}>{formatTwo(totals.abseil)}</td>
          <td className={num}>{formatTwo(totals.litres)}</td>
          <td className={num}>{formatSell(totals.sell, priceShown)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function Breakdown({ option }: { option: PricedOption }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr>
          {['Repaint Breakdown', 'Cost code', 'Detail'].map((h) => (
            <th key={h} className={`${head} text-left`}>{h}</th>
          ))}
          {['Total Hours / Litres', 'Sell Value', 'Cost Value'].map((h) => (
            <th key={h} className={`${head} text-right`}>{h}</th>
          ))}
        </tr>
      </thead>
      {breakdownGroups(option).map((group) => (
        <tbody key={group.code} className="border-t border-slate-200">
          {group.lines.map((line, i) => (
            <tr key={`${line.category}${i}`}>
              <td className="px-2 py-1">{i === 0 ? group.title : ''}</td>
              <td className="px-2 py-1 text-slate-500">{i === 0 ? group.code : ''}</td>
              <td className="px-2 py-1">{line.label}</td>
              <td className={num}>{line.quantity === null ? '' : formatTwo(line.quantity)}</td>
              <td className={num}>{formatMoney(line.sell)}</td>
              <td className={num}>{formatMoney(line.cost)}</td>
            </tr>
          ))}
          <tr className="font-semibold">
            <td className="px-2 py-1" colSpan={4} />
            <td className={num}>{formatMoney(group.sell)}</td>
            <td className={num}>{formatMoney(group.cost)}</td>
          </tr>
        </tbody>
      ))}
      <tbody className="border-t border-slate-200">
        <tr>
          <td className="px-2 py-1">Contract Adjustments</td>
          <td />
          <td className="px-2 py-1">Negotiating Factor</td>
          <td />
          <td className={num}>{formatMoney(option.contractAdjustment)}</td>
          <td />
        </tr>
      </tbody>
    </table>
  );
}

export function SummaryTab({ option, info, gpFloor, blockers, accept }: Props) {
  const floor = gpFloor ?? DEFAULT_GP_FLOOR;
  const priceShown = blockers.length === 0;
  const belowFloor = option.gp * PERCENT < floor;
  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_16rem]">
      <div className="space-y-3">
        <HoursPricedLine info={info} />
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <AreaSummary option={option} info={info} priceShown={priceShown} />
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <Breakdown option={option} />
        </div>
      </div>
      <section className="h-fit rounded-lg border border-slate-200 bg-white p-3 text-sm">
        <h3 className="mb-2 font-semibold text-slate-900">Price</h3>
        <dl className="grid grid-cols-3 gap-2 text-center">
          {[['Sell', formatSell(option.sell, priceShown)], ['G.P.', formatPercent(option.gp)], ['Cost', formatMoney(option.cost)]].map(([label, value]) => (
            <div key={label} className="rounded-md border border-slate-200 p-1.5">
              <dt className="text-xs text-slate-500">{label}</dt>
              <dd className={`font-semibold tabular-nums ${label === 'G.P.' && belowFloor ? 'text-red-700' : 'text-slate-900'}`}>{value}</dd>
            </div>
          ))}
        </dl>
        {!priceShown && <Notice className="mt-3">Complete the REVIEW: the sell price shows once &ldquo;{blockers.join('", "')}&rdquo; is answered Yes.</Notice>}
        <p className="mt-3">Price Check: {option.priceCheckOk ? <span className="text-green-700">OK</span> : <span className="text-red-700">Not OK</span>} — the breakdown adds up to the areas&rsquo; total.</p>
        <p className="mt-2">{belowFloor ? <span className="text-red-700">GP% is below {floor}% — obtain management approval.</span> : <>GP% is {floor}% or more.</>}</p>
        <p className="mt-2 text-xs text-slate-500">Approval is the opportunity&rsquo;s existing CRM steps.</p>
        {accept}
      </section>
    </div>
  );
}
